/**
 * **Audit logs** — two logs on one page, §5.7 and migration 0112.
 *
 * Aaron: "rename the Audit Log section to Audit Logs and add sections to it."
 * So the page is a `RecordTabs` strip — the catalog's header, the one the
 * product already uses when a screen is several lists of the same kind — with
 * **Audit log** first and **Email log** second, and `?tab=` saying which so a
 * link to one is a link to one.
 *
 * **No counts on the tabs**, unlike the catalog's. A count answers "how many
 * of these are there" and the honest answer for a log is "as many as have
 * happened"; the catalog's tabs count a thing somebody curates, which is a
 * different question. The same reasoning kept the card strip off both lists.
 *
 * The first tab is everything below, unchanged.
 *
 * `ListScreen` over the `audit` platform area, which is the whole page. The
 * screen somebody opens here arrives with a question nobody anticipated —
 * *who touched Keystone in March*, *what moved the week the prices changed* —
 * and the list engine's filter builder, date presets, grouping and Export
 * answer those; a hand-rolled search box answers only the questions whoever
 * wrote it thought of.
 *
 * **Three things this screen does not have**, each an absence with a reason:
 *
 *   - **No row menu, no selection, no New.** `platform_audit_log` is
 *     insert-only at the database — two `DO INSTEAD NOTHING` rules — so there
 *     is nothing to offer but reading, and a menu whose every item was
 *     disabled would be worse than none.
 *   - **No card strip.** A card answers "how many of these are there", and for
 *     a log the answer is "as many as have happened". A card counting settings
 *     changes all time is a number that only ever goes up.
 *   - **No detail page.** A row *is* the record; clicking one opens nothing,
 *     because there is nothing behind it. Where a row names a client, the
 *     client's name is the link — which is the navigation somebody actually
 *     wants from here.
 */
import { useNavigate, useSearchParams } from 'react-router-dom'
import type { ListRow } from '../../lib/use-list.js'
import { useList } from '../../lib/use-list.js'
import { adminRequest } from '../../lib/admin-api.js'
import { ListScreen } from '../../components/list-screen.js'
import { Tag, type TagTone } from '../../components/primitives.js'
import { formatListDate } from '../../lib/format.js'
import { RecordTabs } from '../../record/record-tabs.js'
import { EmailLogTab } from './email-log.js'
import { AdminPage, can, useAdminUser } from './shell.js'

/**
 * The area a row belongs to, as a tag — the same seven the activity feed's
 * pills use, from the same `ACTIONS` table on the server.
 *
 * Tinted rather than plain, because this is the column somebody's eye runs
 * down when they are scanning for one kind of thing among a thousand rows.
 */
const AREA: Record<string, { label: string; tone: TagTone }> = {
  sign_in: { label: 'Sign-in', tone: 'quiet' },
  as_client: { label: 'As a client', tone: 'info' },
  setting: { label: 'Settings', tone: 'plain' },
  integration: { label: 'Integrations', tone: 'info' },
  admin_user: { label: 'Admin users', tone: 'warn' },
  client: { label: 'Clients', tone: 'ok' },
  security: { label: 'The platform', tone: 'quiet' },
}

export function AdminAudit() {
  const { me } = useAdminUser()
  const [params, setParams] = useSearchParams()

  /*
   * The email log is behind its own ability (owners and admins), so a support
   * or billing account sees one tab rather than a second one that answers
   * 403. §2.3's rule: the client hides exactly what the server would refuse.
   */
  const mayReadEmail = can(me, 'emailLog')
  const asked = params.get('tab')
  const tab = asked === 'email' && mayReadEmail ? 'email' : 'audit'

  return (
    <AdminPage me={me} active="audit">
      <div className="flex min-h-0 flex-1 flex-col">
        {mayReadEmail && (
          <RecordTabs
            active={tab}
            onChange={(key) => setParams(key === 'audit' ? {} : { tab: key })}
            tabs={[
              { key: 'audit', label: 'Audit log', icon: 'history' },
              { key: 'email', label: 'Email log', icon: 'email' },
            ]}
          />
        )}

        {tab === 'email' ? <EmailLogTab /> : <AuditLogTab />}
      </div>
    </AdminPage>
  )
}

/** The first tab, and what this page was before it had any. */
function AuditLogTab() {
  const navigate = useNavigate()

  const list = useList('audit', {
    /* The Client cell links, so its id has to come back whether shown or not. */
    alwaysFetch: ['id'],
    request: adminRequest,
  })

  return (
    <ListScreen
      area="audit"
      title="Audit log"
      chrome="page"
      /*
       * `cards={false}` explicitly: `chrome="page"` turns the strip on by
       * default, and a log has nothing to count — see the note above.
       */
      cards={false}
      list={list}
      request={adminRequest}
      /*
       * The primary column is *when*, which no other list in the product
       * does. Every other one leads with a name because a person is looking
       * for one record; nobody looks for one audit row, they look at a
       * stretch of time.
       */
      primaryColumn={{
        field: 'at',
        header: 'When',
        /*
         * No secondary line. `who` is a column of its own two cells to the
         * right, and a primary column that repeated it read as the same name
         * twice on every row.
         */
        render: (row: ListRow) => ({ primary: row.at ? when(String(row.at)) : '' }),
      }}
      cell={{
        kind: (row) => {
          const area = AREA[String(row.kind ?? '')]
          return area ? <Tag tone={area.tone}>{area.label}</Tag> : null
        },
        /* Null is "nobody" — a nightly job, a signup. Said, not left blank. */
        who: (row) => (row.who === 'Rowted'
          ? <span className="text-text-faint">a job</span>
          : String(row.who ?? '')),
        client: (row) => (row.client
          ? (
            <button
              type="button"
              onClick={(event) => {
                event.stopPropagation()
                navigate(`/admin/clients/${String(row.tenantId ?? '')}`)
              }}
              className="truncate text-left text-accent hover:text-accent-dark hover:underline"
            >
              {String(row.client)}
            </button>
          )
          : <span className="text-text-faint">—</span>),
        byAPerson: (row) => (row.byAPerson
          ? <Tag tone="plain">A person</Tag>
          : <Tag tone="quiet">A job</Tag>),
      }}
      emptyTitle="Nothing here yet"
      emptyBody="Every sign-in, key change and client a Rowted admin opens lands here."
    />
  )
}

/**
 * `Thu Sep 17 · 9:52 am` — the day in the list's own words, then the time.
 *
 * Twelve-hour and lower-case, the same departure from board 9's 24-hour clock
 * that the activity feed makes and for the same reason: every other time in
 * the product is twelve-hour, and one screen alone using a 24-hour clock reads
 * as a different product.
 */
function when(iso: string): string {
  const at = new Date(iso)
  if (Number.isNaN(at.getTime())) return iso
  const time = at.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })
  return `${formatListDate(at)} · ${time.toLowerCase()}`
}
